import React, { useContext, useEffect } from 'react'
import { JsonTable } from 'react-json-to-html'
import { useTranslation } from 'react-i18next'
import { BellTwoTone, FlagTwoTone, UserOutlined } from '@ant-design/icons'
import { Card, Timeline, Collapse } from 'antd'
import Moment from 'react-moment'
import { toStringToUnderscore, translateJsonKeysByCategory } from '../flag-management.tools'
import { SubModulePerms } from '../../../refactory/modules/access-control/domain/module'
import { Can } from '../../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import EmptyState from '../../../refactory/kernel/infrastructure/empty-state.component'
import { HistoricalContext } from '../../../refactory/modules/jobs/infrastructure/job-detail/historical.context'
import Skeletons from '../../../shared/skeletons'
import RetryRequest from '../../../shared/retry-request'

const Panel = Collapse.Panel
const COLORS = {
  resolved: '#52C41A',
  created: '#EC5B5E',
  viewed: '#F78A4D',
  assigned: '#17a2b8'
}

const TimeLine = (props) => {
  const { t } = useTranslation()
  const { historical, getJobHistorical, errorHistorical } = useContext(HistoricalContext)

  useEffect(() => {
    getJobHistorical(props.jobId)
  }, [])

  const getIcon = (type, status) => {
    switch (type) {
      case 'Flag':
        return <FlagTwoTone twoToneColor={COLORS[status]} />
      case 'Logs':
        return <BellTwoTone twoToneColor={COLORS[status]} />
      case 'User':
        return <UserOutlined style={{ color: '#2196F3' }} />
      default:
        return null
    }
  }

  return historical === undefined && !errorHistorical ? (
    <Skeletons mode='LINES' />
  ) : errorHistorical ? (
    <RetryRequest handleReload={getJobHistorical} />
  ) : (
    <Card title={t('jobs.timeline.title')} bordered={false} className='time-line-card'>
      {historical.length > 0 ? (
        <React.Fragment>
          <div className='time-line-title' />
          <Timeline>
            {historical.map((history, index) => {
              const { action, historicalItem } = history
              let status = null
              let icon = null
              let itemClass = ''
              let dotColor = 'blue'
              let title = t(`jobs-status.${toStringToUnderscore(action)}`)
              if (historicalItem && historicalItem.historable !== 'Job') {
                if (index !== 0) {
                  itemClass = 'remove-dot'
                }
                const actionToArray = action.split(' ')
                const colorStatus = actionToArray.pop()
                const tempTitle = toStringToUnderscore(actionToArray.toString().replace(/,/g, ' '))
                icon = getIcon(historicalItem.historable, colorStatus)
                switch (historicalItem.historable) {
                  case 'Flag':
                    title = t(`flags.${tempTitle}`)
                    status = <b>{t(`flags.${colorStatus}`)}</b>
                    break
                  case 'Task':
                    title = t(`tasks-status.${tempTitle}`)
                    status = <b>{t(`tasks-status.${colorStatus}`)}</b>
                    break
                  case 'Step':
                    title = t(`steps-status.${tempTitle}`)
                    status = <b>{t(`steps-status.${colorStatus}`)}</b>
                    break
                  case 'User':
                    title = t(`user-actions.${action.toUpperCase()}`)
                    break
                  default:
                    title = ' - '
                }
              }

              if (action.toLowerCase().includes('cancelled') || action.toLowerCase().includes('canceled')) {
                dotColor = 'red'
              } else if (action.toLowerCase().includes('finished')) {
                dotColor = 'green'
              }

              return (
                <Timeline.Item key={index} color={dotColor} className={itemClass}>
                  <Collapse>
                    <Panel
                      collapsible={!Can([SubModulePerms.JOB_GENERAL_HISTORY]) ? 'disabled' : 'header'}
                      showArrow={false}
                      header={
                        <div className='collapse-header'>
                          <span className='action-header'>
                            {icon} {title} {status}
                          </span>
                          <span className='date-header'>
                            <Moment tz={props.tz} format='l'>
                              {history.createdAt}
                            </Moment>
                            {' - '}
                            <Moment tz={props.tz} format='LT'>
                              {history.createdAt}
                            </Moment>
                          </span>
                        </div>
                      }
                      key='1'
                    >
                      <JsonTable json={translateJsonKeysByCategory(t, 'jobs.timeline', history.params, props.tz)} />
                    </Panel>
                  </Collapse>
                </Timeline.Item>
              )
            })}
          </Timeline>
        </React.Fragment>
      ) : (
        <EmptyState subtitle={t('empty.no-data')} />
      )}
    </Card>
  )
}

export default TimeLine
